<template>
  <div class="personal-models">
    <div class="heading">
      <div>
        <h2>我的个人模型</h2>
        <p>配置一次自己的模型 Key，在支持的文字对话中使用。厂商调用费用由你的 Key 承担；本期平台不另收模型费用。</p>
      </div>
      <el-button @click="$router.back()">返回</el-button>
    </div>

    <div class="columns">
      <el-card class="form-card">
        <div slot="header">{{ rotatingModelGuid ? '重新验证并更新模型' : '添加模型' }}</div>
        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="模型提供商">
            <el-select v-model="draft.provider" placeholder="选择已接入的提供商" @change="onProviderChange">
              <el-option v-for="(example, provider) in examples" :key="provider" :label="provider" :value="provider" />
            </el-select>
          </el-form-item>
          <el-form-item label="接口地址（填写提供商官方地址）">
            <el-input v-model.trim="draft.baseUrl" placeholder="例如 https://api.openai.com/v1" />
          </el-form-item>
          <el-form-item label="模型型号">
            <el-input v-model.trim="draft.model" :placeholder="selectedExample.modelExample || '填写真实模型型号'" />
          </el-form-item>
          <el-form-item label="显示名称" v-if="!rotatingModelGuid">
            <el-input v-model.trim="displayName" placeholder="例如：我的 GPT 文字模型" maxlength="100" />
          </el-form-item>
          <el-form-item label="API Key">
            <el-input v-model="draft.apiKey" type="password" autocomplete="new-password" show-password
              placeholder="仅用于测试和加密保存；保存后不会回显" />
          </el-form-item>
          <p class="example" v-if="draft.provider">型号示例：{{ selectedExample.modelExample }}；官方接口：{{ selectedExample.baseUrl }}</p>
          <el-alert v-if="proof" title="真实连接测试成功；修改提供商、地址、型号或 Key 后需重新测试" type="success" :closable="false" />
          <el-alert v-if="testCapabilities" type="info" :closable="false">
            <template slot="title">能力状态：文字 {{ stateText(testCapabilities.text) }} · 图像理解 {{ stateText(testCapabilities.vision) }} · 音频 {{ stateText(testCapabilities.audio) }} · 工具调用 {{ stateText(testCapabilities.toolCall) }}</template>
          </el-alert>
          <div class="actions">
            <el-button :loading="testing" @click="testConnection">测试连接</el-button>
            <el-button type="primary" :loading="saving" :disabled="!canSave" @click="save">
              {{ rotatingModelGuid ? '验证并更新' : '保存模型' }}
            </el-button>
            <el-button v-if="rotatingModelGuid" @click="reset">取消更新</el-button>
          </div>
          <p class="note">只有文字能力经过真实调用验证；图像、音频和工具能力未验证时不会标为可用。错误 Key、无余额或不兼容型号均不能保存。</p>
        </el-form>
      </el-card>

      <el-card class="list-card">
        <div slot="header">已验证的个人模型</div>
        <el-empty v-if="!models.length" description="还没有保存个人模型" />
        <div v-for="model in models" :key="model.modelGuid" class="model-card">
          <div class="model-title">{{ model.displayName }} <el-tag size="mini" :type="model.status === 1 ? 'success' : 'info'">{{ model.status === 1 ? '可用' : '已停用' }}</el-tag></div>
          <div class="metadata">{{ model.providerSign }} / {{ model.modelSign }}</div>
          <div class="metadata">Key：{{ model.keyMask }} · 文字：{{ stateText(model.verifiedCapabilities.text) }}</div>
          <div class="model-actions">
            <el-button size="mini" @click="startRotate(model)">更换 Key / 型号</el-button>
            <el-button v-if="model.status === 1" size="mini" type="danger" plain @click="disable(model)">停用</el-button>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import {
  getPersonalModelExamplesApi, verifyPersonalModelDraftApi, savePersonalModelApi,
  rotatePersonalModelApi, listPersonalModelsApi, disablePersonalModelApi,
} from '@/apis/personal-model'
import { personalModelDraftFingerprint, canSaveVerifiedDraft } from '@/utils/personal-model-form.mjs'

const blankDraft = () => ({ provider: '', baseUrl: '', model: '', apiKey: '' })

export default {
  name: 'PersonalModels',
  data() {
    return {
      examples: {}, models: [], draft: blankDraft(), displayName: '',
      proof: null, testCapabilities: null, rotatingModelGuid: '', testing: false, saving: false,
    }
  },
  computed: {
    selectedExample() { return this.examples[this.draft.provider] || {} },
    canSave() { return canSaveVerifiedDraft(this.draft, this.proof) && (this.rotatingModelGuid || this.displayName.trim()) },
  },
  async mounted() {
    await Promise.all([this.loadExamples(), this.loadModels()])
  },
  methods: {
    stateText(state) { return ({ verified: '已验证', unverified: '未验证', unsupported: '不支持' })[state] || '待测试' },
    async loadExamples() {
      const result = await getPersonalModelExamplesApi()
      if (result?.code === 0) this.examples = result.data || {}
    },
    async loadModels() {
      const result = await listPersonalModelsApi()
      if (result?.code === 0) this.models = Array.isArray(result.data) ? result.data : []
    },
    onProviderChange(provider) {
      this.draft.baseUrl = this.examples[provider]?.baseUrl || ''
      this.draft.model = this.examples[provider]?.modelExample || ''
      this.proof = null
      this.testCapabilities = null
    },
    async testConnection() {
      const current = personalModelDraftFingerprint(this.draft)
      this.proof = null
      this.testCapabilities = null
      if (!this.draft.provider || !this.draft.baseUrl || !this.draft.model || !this.draft.apiKey) {
        this.$message.warning('请先填写提供商、接口、型号和 Key')
        return
      }
      this.testing = true
      try {
        const result = await verifyPersonalModelDraftApi(this.draft)
        if (result?.code !== 0 || !result.data?.ticket) throw new Error(result?.msg || '连接测试失败')
        // 测试期间若用户修改了表单，这张票据不能用于另一份配置。
        if (current !== personalModelDraftFingerprint(this.draft)) return
        this.proof = { ticket: result.data.ticket, fingerprint: current }
        this.testCapabilities = result.data.capabilities
        this.$message.success('模型真实连接测试成功，可以保存')
      } catch (error) {
        this.$message.error(error?.message || '连接失败，请检查 Key、型号、接口和账户额度')
      } finally {
        this.testing = false
      }
    },
    async save() {
      if (!this.canSave) return
      this.saving = true
      try {
        const payload = { ...this.draft, displayName: this.displayName, ticket: this.proof.ticket }
        const result = this.rotatingModelGuid
          ? await rotatePersonalModelApi({ ...payload, modelGuid: this.rotatingModelGuid })
          : await savePersonalModelApi(payload)
        if (result?.code !== 0) throw new Error(result?.msg || '保存失败，请重新测试连接')
        this.$message.success('个人模型已加密保存')
        this.reset()
        await this.loadModels()
      } catch (error) {
        this.proof = null // 票据一次性消费或已过期，绝不让用户误以为还能直接重试保存。
        this.$message.error(error?.message || '保存失败，请重新测试连接')
      } finally {
        this.saving = false
      }
    },
    startRotate(model) {
      this.rotatingModelGuid = model.modelGuid
      this.displayName = model.displayName
      this.draft = { provider: model.providerSign, baseUrl: model.baseUrl, model: model.modelSign, apiKey: '' }
      this.proof = null
      this.testCapabilities = null
    },
    reset() {
      this.rotatingModelGuid = ''
      this.draft = blankDraft() // 明文 Key 只保留在当前表单，完成后立刻清掉。
      this.displayName = ''
      this.proof = null
      this.testCapabilities = null
    },
    async disable(model) {
      try {
        await this.$confirm('停用后，所有正在选择该个人模型的新对话将无法继续使用。确认停用？', '确认停用')
        const result = await disablePersonalModelApi(model.modelGuid)
        if (result?.code !== 0) throw new Error(result?.msg || '停用失败')
        await this.loadModels()
      } catch (error) {
        if (error !== 'cancel') this.$message.error(error?.message || '停用失败')
      }
    },
  },
}
</script>

<style scoped>
.personal-models { padding: 28px; height: 100%; overflow: auto; background: #f7f8fc; }
.heading { display: flex; justify-content: space-between; align-items: start; margin-bottom: 24px; }
.heading h2 { margin: 0 0 8px; }.heading p, .note, .example, .metadata { color: #687084; }
.columns { display: grid; grid-template-columns: minmax(420px, 1.1fr) minmax(320px, 1fr); gap: 20px; }
.form-card, .list-card { min-width: 0; }.form-card .el-select { width: 100%; }
.actions { display: flex; gap: 8px; margin: 20px 0; }.note, .example { font-size: 12px; line-height: 1.7; }
.model-card { padding: 16px 0; border-bottom: 1px solid #edf0f5; }.model-title { font-weight: 600; margin-bottom: 8px; }
.metadata { font-size: 12px; line-height: 1.8; }.model-actions { margin-top: 10px; }
@media (max-width: 950px) { .columns { grid-template-columns: 1fr; } }
</style>
